import React from 'react';
import { motion } from 'framer-motion';
import ScrollReveal from '../components/common/ScrollReveal';
import { HiOutlineLightBulb, HiOutlineHome, HiOutlineSparkles } from 'react-icons/hi';
import aboutImg from '../assets/images/projects/modern-villa-angle.jpg';
import aboutImg2 from '../assets/images/projects/sohel-house.jpg';
import heroBg from '../assets/images/projects/arabian-villa.jpg';

const stats = [
  { number: '50+', label: 'Projects Completed' },
  { number: '5+', label: 'Years of Experience' },
  { number: '60+', label: 'Happy Clients' },
  { number: '5+', label: 'Design Awards' },
];

const values = [
  {
    icon: <HiOutlineLightBulb className="text-3xl" />,
    title: 'Innovative Vision',
    desc: 'Every space tells a story. We bring fresh, creative perspectives to each project, ensuring designs that are both functional and extraordinary.',
  },
  {
    icon: <HiOutlineHome className="text-3xl" />,
    title: 'Crafted Excellence',
    desc: 'From concept to completion, we maintain the highest standards of quality, paying meticulous attention to every detail that defines your space.',
  },
  {
    icon: <HiOutlineSparkles className="text-3xl" />,
    title: 'Timeless Design',
    desc: 'We believe in creating spaces that transcend trends — designs that feel as relevant and inspiring decades from now as they do today.',
  },
];

export default function AboutPage() {
  return (
    <>
      {/* ======================== PAGE HERO ======================== */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroBg} alt="Arabian heritage villa" fetchpriority="high" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-900/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-transparent to-navy-900/50" />
        </div>
        <div className="relative z-10 text-center px-6 pt-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-tag"
          >
            About Us
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-4"
          >
            Where Creativity
            <br />
            <span className="font-accent italic text-accent">Meets Structure</span>
          </motion.h1>
        </div>
      </section>

      {/* ======================== ABOUT CONTENT ======================== */}
      <section className="section-padding bg-navy-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <ScrollReveal direction="left">
              <div className="relative">
                <div className="relative overflow-hidden rounded-3xl">
                  <img
                    src={aboutImg}
                    alt="IBDA'A Design Studio project"
                    loading="lazy"
                    className="w-full h-[550px] object-cover"
                  />
                </div>
                {/* Accent frame */}
                <div className="absolute -bottom-4 -right-4 w-full h-full border-2 border-accent/30 rounded-3xl -z-10" />
                {/* Experience badge */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="absolute -bottom-6 -left-6 bg-accent text-white p-6 rounded-2xl shadow-2xl"
                >
                  <span className="block font-display text-4xl font-bold">5+</span>
                  <span className="font-body text-sm text-white/80">Years of<br />Excellence</span>
                </motion.div>
              </div>
            </ScrollReveal>

            {/* Text Side */}
            <div>
              <ScrollReveal>
                <span className="section-tag">Our Story</span>
              </ScrollReveal>

              <ScrollReveal delay={0.1}>
                <h2 className="section-title mt-4 mb-6 text-3xl md:text-4xl lg:text-5xl">
                  Transforming Ideas into
                  <br />
                  <span className="font-accent italic text-accent">Iconic Spaces</span>
                </h2>
              </ScrollReveal>

              <ScrollReveal delay={0.2}>
                <div className="accent-line mb-8" />
              </ScrollReveal>

              <ScrollReveal delay={0.3}>
                <p className="font-body text-white/50 leading-relaxed text-lg mb-6">
                  <strong className="text-white">IBDA'A Design Studio</strong> is a
                  distinguished architecture and interior design practice dedicated to
                  creating spaces that resonate with purpose, beauty, and innovation. The
                  word <em className="text-accent">"Ibda'a"</em> means{' '}
                  <em className="text-accent">"creativity"</em> in Arabic — and that
                  ethos is woven into every project we undertake.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.4}>
                <p className="font-body text-white/50 leading-relaxed text-lg mb-6">
                  With over five years of experience in architectural design and interior
                  styling, our studio has delivered a diverse portfolio spanning residential
                  homes, commercial spaces, luxury interiors, and urban planning projects.
                  We believe that great design is not just about aesthetics — it is about
                  creating environments that enhance life, elevate mood, and stand the test
                  of time.
                </p>
              </ScrollReveal>

              <ScrollReveal delay={0.5}>
                <p className="font-body text-white/50 leading-relaxed text-lg">
                  Our approach is deeply collaborative. We listen, we envision, and we
                  build — transforming raw concepts into refined realities. Every line drawn
                  and every material chosen reflects our unwavering commitment to quality,
                  sustainability, and the unique story of each client.
                </p>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* ======================== STATS ======================== */}
      <section className="bg-navy-800 py-20">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <ScrollReveal key={stat.label} delay={i * 0.15} direction="up">
                <div className="text-center">
                  <motion.span
                    className="block font-display text-4xl md:text-5xl font-bold text-accent mb-2"
                    whileHover={{ scale: 1.1 }}
                  >
                    {stat.number}
                  </motion.span>
                  <span className="font-body text-white/50 text-sm tracking-[0.15em] uppercase">
                    {stat.label}
                  </span>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== VALUES ======================== */}
      <section className="section-padding bg-navy-900">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal>
            <div className="text-center mb-16">
              <span className="section-tag">Our Philosophy</span>
              <h2 className="section-title mt-4">
                What Drives <span className="font-accent italic text-accent">Us</span>
              </h2>
              <div className="accent-line mx-auto mt-6" />
            </div>
          </ScrollReveal>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((val, i) => (
              <ScrollReveal key={val.title} delay={i * 0.15} direction="up">
                <motion.div
                  whileHover={{ y: -8 }}
                  transition={{ duration: 0.3 }}
                  className="card-dark p-10 group text-center"
                >
                  <div className="w-16 h-16 flex items-center justify-center bg-accent/10 text-accent rounded-2xl mb-6 mx-auto group-hover:bg-accent group-hover:text-white transition-all duration-300">
                    {val.icon}
                  </div>
                  <h3 className="font-display text-xl font-bold text-white mb-4 group-hover:text-accent transition-colors">
                    {val.title}
                  </h3>
                  <p className="font-body text-white/50 leading-relaxed">
                    {val.desc}
                  </p>
                </motion.div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ======================== VISION SECTION ======================== */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src={aboutImg2} alt="" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-navy-900/85" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <ScrollReveal>
            <span className="section-tag">Our Vision</span>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4 mb-6 leading-tight">
              From Vision to Landmark —
              <br />
              <span className="font-accent italic text-accent">The Art of Architecture</span>
            </h2>
            <p className="font-body text-white/50 text-lg leading-relaxed">
              We envision a future where every built environment reflects the highest
              aspirations of its inhabitants. Our mission is to push the boundaries of
              architectural innovation while honoring the timeless principles of design
              that make spaces truly remarkable.
            </p>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
}
